import React, { useMemo } from 'react';
import { Div, Text } from '@pinpoint/design-kit';
import PropTypes from 'prop-types';

import Legend from 'src/infographics/roi_calculator_v2/legend';

const actuarialInstructions = (
	<>
		<Text
			textWeight="400"
			p={{ t: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			This application compares your baseline growth without Pinpoint over the next year (shown in light blue), with
			your growth if you use Pinpoint risk selection over this same time period and also continue to use your current
			pricing (shown in light green).
		</Text>
		<Text
			textWeight="400"
			p={{ t: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			For &quot;Yearly direct loss and adjustment expenses&quot;, please enter the total loss dollars and loss
			adjustment expenses across your book over the past year, pertaining to the specific peril(s) indicated in the
			&quot;Risk model&quot; field. For &quot;Policies in force&quot;, enter the current number. The &quot;Average
			yearly premium&quot; equals the dollar price for one year of coverage for the average policy.
		</Text>
		<Text
			textWeight="400"
			p={{ y: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			&quot;Yearly cancellation rate&quot; equals the percentage of your book that you believe will choose to cancel
			their policies over the next year. &quot;Carrier non-renewal&quot; refers to the maximum percentage of policies
			that you may decide, directly or indirectly, not to renew over the next year. &quot;New-business rate&quot; is a
			percentage equal to the number of new policies projected over the next year, divided by the current number of
			policies in force. The &quot;Net growth rate&quot; equals the new-business rate minus the cancellation and
			carrier-non-renewal rates.
		</Text>
	</>
);

const classifierInstructions = (
	<>
		<Text
			textWeight="400"
			p={{ t: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			This application compares your baseline growth without Pinpoint over the next year (shown in light blue), with
			your growth if you do use Pinpoint risk selection over this same time period (shown in light green), for the Risk
			Model indicated in the upper-righthand cell.
		</Text>
		<Text
			textWeight="400"
			p={{ t: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			For &quot;Yearly direct loss and adjustment expenses&quot;, please enter the total loss dollars and loss
			adjustment expenses across your book over the past year, pertaining to the specific risk indicated in the
			&quot;Risk model&quot; field. For &quot;Policies in force&quot;, enter the current number. The &quot;Average
			yearly premium&quot; equals the dollar price for one year of coverage for the average policy.
		</Text>
		<Text
			textWeight="400"
			p={{ y: '0.5rem' }}
			textColor="shade4D"
			textSize="input"
			style={{ textIndent: '2rem', lineHeight: '1rem' }}
		>
			&quot;Yearly cancellation rate&quot; equals the percentage of your book that you believe will choose to cancel
			their policies over the next year. &quot;Carrier non-renewal&quot; refers to the maximum percentage of policies
			that you may decide, directly or indirectly, not to renew over the next year. &quot;New-business rate&quot; is a
			percentage equal to the number of new policies projected over the next year, divided by the current number of
			policies in force. The &quot;Net growth rate&quot; equals the new-business rate minus the cancellation and
			carrier-non-renewal rates.
		</Text>
	</>
);

function Header({ type, corporateLayout, }) {
	const instructions = useMemo(() => (type === 'CLASSIFIER' ? classifierInstructions : actuarialInstructions), [type]);

	return (
		<Div d="flex" w={corporateLayout ? "40%" : "50%"} flexDir="column" justify="space-between" data-test="calc-left-header">
			<Div d="flex" flexDir="column" data-test="calc-intsr-div">
				<Text textWeight="300" m={{ b: '.25rem' }} textSize="paragraph" textColor="tealLight">
					ROI Calculator
				</Text>
				<Text textWeight="600" textColor="shade4D" textSize="input">
					Instructions:
				</Text>
				<Div maxH="14.25rem" overflow="auto">
					{instructions}
				</Div>
			</Div>
			<Div borderColor="shadeAF" border={{ t: '1px solid' }} data-test="calc-legend-div">
				<Text textWeight="600" p={{ y: '0.5rem' }} textSize="small" textColor="shade4D">
					Legend:
				</Text>
				<Div d="flex">
					<Legend backgroundColor="brand500">
						Client
						<br />
						Inputs
					</Legend>
					<Legend backgroundColor="skyBlue">Client Only Growth</Legend>
					<Legend backgroundColor="brand200">Lift from Pinpoint</Legend>
					<Legend backgroundColor="tealLight">Growth with Pinpoint</Legend>
					<Legend backgroundColor="greenDark2">
						Client
						<br />
						Savings
					</Legend>
				</Div>
			</Div>
		</Div>
	);
}

Header.propTypes = {
	type: PropTypes.string.isRequired,
};

Header.defaultProps = {};

export default Header;
